<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="用户编号" prop="userId">
        <el-input v-model="queryParams.userId" class="!w-160px" clearable @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-160px" clearable>
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.BROKERAGE_WITHDRAW_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="提现类型" prop="type">
        <el-select v-model="queryParams.type" class="!w-160px" clearable>
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.BROKERAGE_WITHDRAW_TYPE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="申请时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          class="!w-240px"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始"
          end-placeholder="结束"
          :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column align="center" label="编号" prop="id" width="70" />
      <el-table-column align="left" label="用户" min-width="120">
        <template #default="{ row }">
          <div>ID：{{ row.userId }}</div>
          <div>{{ row.userNickname || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column align="left" label="金额" min-width="120">
        <template #default="{ row }">
          <div>金额：￥{{ fenToYuan(row.price || 0) }}</div>
          <div>手续费：￥{{ fenToYuan(row.feePrice || 0) }}</div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="方式" width="100">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.BROKERAGE_WITHDRAW_TYPE" :value="row.type" />
        </template>
      </el-table-column>
      <el-table-column align="left" label="提现信息" min-width="140">
        <template #default="{ row }">
          <div v-if="row.userAccount">账号：{{ row.userAccount }}</div>
          <div v-if="row.userName">姓名：{{ row.userName }}</div>
          <div v-if="row.bankName">银行：{{ row.bankName }}</div>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="申请时间"
        prop="createTime"
        :formatter="dateFormatter"
        width="170"
      />
      <el-table-column align="left" label="状态" min-width="140">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.BROKERAGE_WITHDRAW_STATUS" :value="row.status" />
          <div v-if="row.transferErrorMsg" class="text-xs text-red-500">
            {{ row.transferErrorMsg }}
          </div>
          <div v-if="row.payTransferId" class="text-xs text-gray-400">
            转账 gs-{{ row.id }} / {{ row.payTransferId }}
          </div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" fixed="right" width="150">
        <template #default="{ row }">
          <template v-if="row.status === 0 && !row.payTransferId">
            <el-button
              v-hasPermi="['distribution:shareholder:withdraw:audit']"
              link
              type="primary"
              @click="handleApprove(row.id)"
            >
              通过
            </el-button>
            <el-button
              v-hasPermi="['distribution:shareholder:withdraw:audit']"
              link
              type="danger"
              @click="openReject(row)"
            >
              驳回
            </el-button>
          </template>
          <el-button
            v-if="row.status === 21"
            v-hasPermi="['distribution:shareholder:withdraw:audit']"
            link
            type="warning"
            @click="handleRetry(row.id)"
          >
            重新转账
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="rejectVisible" title="驳回提现" width="420px">
    <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="驳回原因" />
    <template #footer>
      <el-button type="primary" :loading="rejectLoading" @click="submitReject">确 定</el-button>
      <el-button @click="rejectVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { fenToYuan } from '@/utils'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import * as WithdrawApi from '@/api/mall/trade/shareholder/withdraw'

defineOptions({ name: 'TradeShareholderWithdraw' })

const message = useMessage()
const loading = ref(false)
const list = ref<WithdrawApi.ShareholderWithdrawVO[]>([])
const total = ref(0)
const queryFormRef = ref()
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined as string | undefined,
  status: undefined as number | undefined,
  type: undefined as number | undefined,
  createTime: undefined as string[] | undefined
})

const rejectVisible = ref(false)
const rejectLoading = ref(false)
const rejectReason = ref('')
const rejectId = ref<number>()

const getList = async () => {
  loading.value = true
  try {
    const params = {
      ...queryParams,
      userId: queryParams.userId ? Number(queryParams.userId) : undefined
    }
    const data = await WithdrawApi.getShareholderWithdrawPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const handleApprove = async (id?: number) => {
  if (!id) return
  await message.confirm('确认通过该提现申请？')
  await WithdrawApi.approveShareholderWithdraw(id)
  message.success('已通过')
  await getList()
}

const openReject = (row: WithdrawApi.ShareholderWithdrawVO) => {
  rejectId.value = row.id
  rejectReason.value = ''
  rejectVisible.value = true
}

const submitReject = async () => {
  rejectLoading.value = true
  try {
    await WithdrawApi.rejectShareholderWithdraw({
      id: rejectId.value!,
      auditReason: rejectReason.value
    })
    message.success('已驳回')
    rejectVisible.value = false
    await getList()
  } finally {
    rejectLoading.value = false
  }
}

const handleRetry = async (id?: number) => {
  if (!id) return
  await message.confirm('确认重新发起转账？')
  await WithdrawApi.reTransferShareholderWithdraw(id)
  message.success('已重新提交转账')
  await getList()
}

onMounted(getList)
</script>
